import React, { useCallback, useEffect, useMemo, useState } from 'react';
import FdModal from '../components/FdModal';
import {
  productSetupApi,
  resolveProductSetupApiError,
} from '../services/productSetupApi';
import useScopePermissions from '../hooks/useScopePermissions';
import { todayIso, triggerCsvDownload, triggerExcelLikeDownload } from '../utils/exportUtils';
import TablePagination, { usePagination } from '../components/TablePagination';
import './ProductSetupPage.css';

const COMMISSION_FIELDS = [
  { key: 'sa_commission', label: 'SA Commission (%)' },
  { key: 'um_commission', label: 'UM Commission (%)' },
  { key: 'bm_commission', label: 'BM Commission (%)' },
  { key: 'aah_commission', label: 'AAH Commission (%)' },
  { key: 'ah_commission', label: 'AH Commission (%)' },
  { key: 'sah_commission', label: 'SAH Commission (%)' },
  { key: 'dh_commission', label: 'DH Commission (%)' },
  { key: 'ceo_commission', label: 'CEO Commission (%)' },
];

const EMPTY_PLAN = { plan_name: '', plan_premium: '' };

function createEmptyFormData() {
  return {
    product_name: '',
    is_active: true,
    plans: [{ ...EMPTY_PLAN }],
    sa_commission: '',
    um_commission: '',
    bm_commission: '',
    aah_commission: '',
    ah_commission: '',
    sah_commission: '',
    dh_commission: '',
    ceo_commission: '',
    notes: '',
  };
}

function parseNullableNumber(value) {
  if (value === null || value === undefined) {
    return null;
  }

  const normalized = String(value).trim();
  if (normalized === '') {
    return null;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizePlansForPayload(plans) {
  if (!Array.isArray(plans)) {
    return [];
  }

  return plans
    .map((plan) => ({
      plan_name: String(plan.plan_name || '').trim(),
      plan_premium: parseNullableNumber(plan.plan_premium),
    }))
    .filter((plan) => plan.plan_name !== '');
}

export default function ProductSetupPage() {
  const { hasScope } = useScopePermissions();
  const canView = hasScope('product-setup.view');
  const canCreate = hasScope('product-setup.create');
  const canUpdate = hasScope('product-setup.update');
  const canDelete = hasScope('product-setup.delete');

  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
  });
  const [viewMode, setViewMode] = useState('table');
  const [viewingProductId, setViewingProductId] = useState(null);
  const [formData, setFormData] = useState(createEmptyFormData);

  const loadProducts = useCallback(async (options = {}) => {
    const { clearFeedback = true } = options;

    setIsLoading(true);
    setError('');
    if (clearFeedback) {
      setMessage('');
    }

    try {
      const response = await productSetupApi.listProductCommissions({ bustCache: true });
      const list = response?.data?.product_commissions || response?.product_commissions || [];
      setProducts(Array.isArray(list) ? list : []);

      if (clearFeedback && list.length === 0) {
        setMessage('No products found.');
      }
    } catch (err) {
      setProducts([]);
      setError(resolveProductSetupApiError(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const search = filters.search.toLowerCase();
      const matchesSearch = product.product_name?.toLowerCase().includes(search) ||
        product.notes?.toLowerCase().includes(search);
      const status = product.is_active ? 'Active' : 'Inactive';
      const matchesStatus = filters.status === 'all' || status === filters.status;

      return matchesSearch && matchesStatus;
    });
  }, [products, filters]);

  const pagination = usePagination(filteredProducts);

  const viewingProduct = useMemo(
    () => products.find((product) => Number(product.id) === Number(viewingProductId)) || null,
    [products, viewingProductId]
  );

  function handleFilterChange(event) {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  }

  const EXPORT_HEADERS = ['Product', 'Status', 'Plans', 'SA %', 'UM %', 'BM %', 'AAH %', 'AH %', 'SAH %', 'DH %', 'CEO %', 'Notes', 'Created At'];

  function buildExportRows() {
    return filteredProducts.map((product) => [
      product.product_name || '',
      product.is_active ? 'Active' : 'Inactive',
      Array.isArray(product.plans) ? product.plans.map((plan) => plan.plan_name).join(', ') : '',
      product.sa_commission ?? '',
      product.um_commission ?? '',
      product.bm_commission ?? '',
      product.aah_commission ?? '',
      product.ah_commission ?? '',
      product.sah_commission ?? '',
      product.dh_commission ?? '',
      product.ceo_commission ?? '',
      product.notes || '',
      product.created_at || '',
    ]);
  }

  function exportCsv() {
    triggerCsvDownload(`product-setup-${todayIso()}.csv`, EXPORT_HEADERS, buildExportRows());
  }

  function exportExcel() {
    triggerExcelLikeDownload(`product-setup-${todayIso()}.xls`, EXPORT_HEADERS, buildExportRows());
  }

  function handleFormChange(event) {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }

  function handleStatusToggle(event) {
    const { checked } = event.target;
    setFormData((prev) => ({ ...prev, is_active: Boolean(checked) }));
  }

  function handlePlanChange(index, field, value) {
    setFormData((prev) => ({
      ...prev,
      plans: prev.plans.map((plan, planIndex) => (
        planIndex === index
          ? { ...plan, [field]: value }
          : plan
      )),
    }));
  }

  function addPlanRow() {
    setFormData((prev) => ({
      ...prev,
      plans: [...prev.plans, { ...EMPTY_PLAN }],
    }));
  }

  function removePlanRow(index) {
    setFormData((prev) => {
      if (prev.plans.length === 1) {
        return {
          ...prev,
          plans: [{ ...EMPTY_PLAN }],
        };
      }

      return {
        ...prev,
        plans: prev.plans.filter((_, planIndex) => planIndex !== index),
      };
    });
  }

  function openAddForm() {
    if (!canCreate) {
      setError('You do not have permission to add products.');
      return;
    }

    setIsEditMode(false);
    setSelectedProduct(null);
    setError('');
    setFormData(createEmptyFormData());
    setIsFormOpen(true);
  }

  function openEditForm(product) {
    if (!canUpdate) {
      setError('You do not have permission to edit products.');
      return;
    }

    const normalizedPlans = Array.isArray(product.plans) && product.plans.length > 0
      ? product.plans.map((plan) => ({
        plan_name: plan.plan_name || '',
        plan_premium: plan.plan_premium ?? '',
      }))
      : [{ ...EMPTY_PLAN }];

    setIsEditMode(true);
    setSelectedProduct(product);
    setError('');
    setFormData({
      product_name: product.product_name || '',
      is_active: product.is_active !== false,
      plans: normalizedPlans,
      sa_commission: product.sa_commission ?? '',
      um_commission: product.um_commission ?? '',
      bm_commission: product.bm_commission ?? '',
      aah_commission: product.aah_commission ?? '',
      ah_commission: product.ah_commission ?? '',
      sah_commission: product.sah_commission ?? '',
      dh_commission: product.dh_commission ?? '',
      ceo_commission: product.ceo_commission ?? '',
      notes: product.notes || '',
    });
    setIsFormOpen(true);
  }

  function closeForm() {
    setIsFormOpen(false);
    setSelectedProduct(null);
    setFormData(createEmptyFormData());
  }

  function openViewDetails(product) {
    if (!canView) {
      setError('You do not have permission to view product details.');
      return;
    }

    setViewingProductId(product.id);
  }

  function closeViewDetails() {
    setViewingProductId(null);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    setMessage('');

    if (isEditMode && !canUpdate) {
      setError('You do not have permission to edit products.');
      return;
    }

    if (!isEditMode && !canCreate) {
      setError('You do not have permission to add products.');
      return;
    }

    const productName = String(formData.product_name || '').trim();
    if (productName === '') {
      setError('Product name is required.');
      return;
    }

    const payload = {
      product_name: productName,
      is_active: Boolean(formData.is_active),
      plans: normalizePlansForPayload(formData.plans),
      notes: String(formData.notes || '').trim() || null,
    };

    COMMISSION_FIELDS.forEach((field) => {
      payload[field.key] = parseNullableNumber(formData[field.key]);
    });

    setIsSubmitting(true);

    try {
      if (isEditMode) {
        await productSetupApi.updateProductCommission(selectedProduct.id, payload);
        setMessage('Product updated successfully.');
      } else {
        await productSetupApi.createProductCommission(payload);
        setMessage('Product created successfully.');
      }

      closeForm();
      await loadProducts({ clearFeedback: false });
    } catch (err) {
      setError(resolveProductSetupApiError(err));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleDelete(product) {
    if (!canDelete) {
      setError('You do not have permission to delete products.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${product.product_name}"?`)) return;

    setError('');
    setMessage('');
    setIsSubmitting(true);

    try {
      await productSetupApi.deleteProductCommission(product.id);
      setMessage('Product deleted successfully.');
      await loadProducts({ clearFeedback: false });
    } catch (err) {
      setError(resolveProductSetupApiError(err));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="dashboard-container">
      <section className="dashboard-header">
        <h1>Product Setup</h1>
        <p>Maintain products, plans, and commission rates per hierarchy role.</p>
      </section>

      <section className="dashboard-content">
        <div className="dashboard-toolbar">
          <div className="dashboard-filters">
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Search product name or notes..."
            />
            <select name="status" value={filters.status} onChange={handleFilterChange}>
              <option value="all">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
          <div className="product-toolbar-actions">
            <button type="button" className="profile-secondary-btn" onClick={exportCsv}>
              Export CSV
            </button>
            <button type="button" className="profile-secondary-btn" onClick={exportExcel}>
              Export Excel
            </button>
            <div className="product-view-toggle" role="group" aria-label="View mode">
              <button
                type="button"
                className={`product-view-toggle-btn ${viewMode === 'table' ? 'is-active' : ''}`}
                onClick={() => setViewMode('table')}
                title="Table view"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="16" rx="2" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                  <line x1="9" y1="4" x2="9" y2="20" />
                </svg>
              </button>
              <button
                type="button"
                className={`product-view-toggle-btn ${viewMode === 'cards' ? 'is-active' : ''}`}
                onClick={() => setViewMode('cards')}
                title="Card view"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="8" height="7" rx="1.5" />
                  <rect x="13" y="4" width="8" height="7" rx="1.5" />
                  <rect x="3" y="13" width="8" height="7" rx="1.5" />
                  <rect x="13" y="13" width="8" height="7" rx="1.5" />
                </svg>
              </button>
            </div>
            {canCreate ? (
              <button type="button" className="profile-secondary-btn" onClick={openAddForm}>
                + Add Product
              </button>
            ) : null}
          </div>
        </div>

        {error && !isFormOpen && <div className="error-box">{error}</div>}
        {message && <div className="success-box">{message}</div>}

        {isLoading ? (
          <div className="dashboard-loading">Loading products...</div>
        ) : filteredProducts.length === 0 ? (
          <div className="dashboard-empty">No products found.</div>
        ) : viewMode === 'table' ? (
          <div className="dashboard-table-wrap">
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>Actions</th>
                  <th>Product</th>
                  <th>Status</th>
                  <th>Plans</th>
                  <th>SA%</th>
                  <th>UM%</th>
                  <th>BM%</th>
                  <th>AAH%</th>
                  <th>AH%</th>
                  <th>SAH%</th>
                  <th>DH%</th>
                  <th>CEO%</th>
                  <th>Notes</th>
                  <th>Created At</th>
                </tr>
              </thead>
              <tbody>
                {pagination.pagedItems.map((product) => (
                  <tr key={product.id}>
                    <td>
                      <div className="dashboard-actions">
                        {canView ? (
                          <button
                            type="button"
                            className="profile-secondary-btn profile-secondary-btn-xs"
                            onClick={() => openViewDetails(product)}
                          >
                            View
                          </button>
                        ) : null}
                        {canUpdate ? (
                          <button
                            type="button"
                            className="profile-secondary-btn profile-secondary-btn-xs"
                            onClick={() => openEditForm(product)}
                          >
                            Edit
                          </button>
                        ) : null}
                        {canDelete ? (
                          <button
                            type="button"
                            className="profile-danger-btn profile-danger-btn-xs"
                            onClick={() => handleDelete(product)}
                            disabled={isSubmitting}
                          >
                            Delete
                          </button>
                        ) : null}
                        {!canView && !canUpdate && !canDelete ? <span>-</span> : null}
                      </div>
                    </td>
                    <td>{product.product_name}</td>
                    <td>
                      <span className={`status-badge status-${product.is_active ? 'active' : 'inactive'}`}>
                        {product.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      {Array.isArray(product.plans) && product.plans.length > 0
                        ? product.plans.map((plan) => plan.plan_name).join(', ')
                        : '-'}
                    </td>
                    <td>{product.sa_commission ?? '-'}</td>
                    <td>{product.um_commission ?? '-'}</td>
                    <td>{product.bm_commission ?? '-'}</td>
                    <td>{product.aah_commission ?? '-'}</td>
                    <td>{product.ah_commission ?? '-'}</td>
                    <td>{product.sah_commission ?? '-'}</td>
                    <td>{product.dh_commission ?? '-'}</td>
                    <td>{product.ceo_commission ?? '-'}</td>
                    <td>{product.notes || '-'}</td>
                    <td>{product.created_at || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="product-card-grid">
            {pagination.pagedItems.map((product) => (
              <div key={product.id} className="product-card">
                <div className="product-card-head">
                  <div className="product-card-title-wrap">
                    <h3>{product.product_name}</h3>
                    {product.notes ? <p>{product.notes}</p> : null}
                  </div>
                  <span className={`status-badge status-${product.is_active ? 'active' : 'inactive'}`}>
                    {product.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div className="product-card-section">
                  <p className="product-card-label">Plans</p>
                  {Array.isArray(product.plans) && product.plans.length > 0 ? (
                    <div className="product-plan-tags">
                      {product.plans.map((plan, index) => (
                        <span key={`${product.id}-plan-${index}`} className="product-plan-tag">
                          {plan.plan_name}
                          {plan.plan_premium != null ? ` - PHP ${Number(plan.plan_premium).toLocaleString()}` : ''}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="product-card-empty">No plans configured</p>
                  )}
                </div>

                <div className="product-rate-grid">
                  {COMMISSION_FIELDS.map((field) => (
                    <div key={`${product.id}-${field.key}`} className="product-rate-cell">
                      <span>{field.label.replace(' Commission (%)', '')}</span>
                      <strong>{product[field.key] != null ? `${product[field.key]}%` : '-'}</strong>
                    </div>
                  ))}
                </div>

                <div className="product-card-actions">
                  {canView ? (
                    <button
                      type="button"
                      className="profile-secondary-btn profile-secondary-btn-sm"
                      onClick={() => openViewDetails(product)}
                    >
                      View
                    </button>
                  ) : null}
                  {canUpdate ? (
                    <button
                      type="button"
                      className="profile-secondary-btn profile-secondary-btn-sm"
                      onClick={() => openEditForm(product)}
                    >
                      Edit
                    </button>
                  ) : null}
                  {canDelete ? (
                    <button
                      type="button"
                      className="profile-danger-btn profile-danger-btn-sm"
                      onClick={() => handleDelete(product)}
                      disabled={isSubmitting}
                    >
                      Delete
                    </button>
                  ) : null}
                  {!canView && !canUpdate && !canDelete ? <span>-</span> : null}
                </div>
              </div>
            ))}
          </div>
        )}

        {!isLoading ? <TablePagination {...pagination} /> : null}
      </section>

      <FdModal
        isOpen={isFormOpen}
        onClose={closeForm}
        eyebrow="Product Setup"
        title={isEditMode ? 'Edit Product' : 'Create Product'}
        subtitle="Configure product plans and commission rates."
        wide
        footer={(
          <>
            <button type="button" className="fd-btn fd-btn-cancel" onClick={closeForm} disabled={isSubmitting}>
              Cancel
            </button>
            <button
              type="submit"
              form="product-setup-form"
              className="fd-btn fd-btn-primary"
              disabled={isSubmitting || (isEditMode ? !canUpdate : !canCreate)}
            >
              {isSubmitting ? 'Saving...' : (isEditMode ? 'Update Product' : 'Create Product')}
            </button>
          </>
        )}
      >
        <form id="product-setup-form" onSubmit={handleSubmit}>
          <p className="fd-section-title">Product Details</p>
          <div className="fd-modal-grid">
            <div className="fd-field fd-field-wide">
              <label htmlFor="product_name">Product Name *</label>
              <input
                id="product_name"
                type="text"
                name="product_name"
                value={formData.product_name}
                onChange={handleFormChange}
                placeholder="Enter product name"
                required
              />
            </div>

            <div className="fd-field fd-field-wide">
              <div className="product-status-card">
                <div className="product-status-copy">
                  <p className="product-status-title">Active</p>
                  <p className="product-status-subtitle">Enable this product for use in collections</p>
                </div>
                <label className="product-switch" htmlFor="is_active_toggle">
                  <input
                    id="is_active_toggle"
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={handleStatusToggle}
                  />
                  <span className="product-switch-slider" />
                </label>
              </div>
            </div>
          </div>

          <p className="fd-section-title">Plans</p>
          <div className="fd-modal-grid">
            {formData.plans.map((plan, index) => (
              <div className="fd-field fd-field-wide" key={`plan-${index}`}>
                <div className="product-plan-row">
                  <input
                    type="text"
                    value={plan.plan_name}
                    onChange={(event) => handlePlanChange(index, 'plan_name', event.target.value)}
                    placeholder="Plan name"
                  />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={plan.plan_premium}
                    onChange={(event) => handlePlanChange(index, 'plan_premium', event.target.value)}
                    placeholder="Premium amount"
                  />
                  <button
                    type="button"
                    className="profile-danger-btn profile-danger-btn-xs"
                    onClick={() => removePlanRow(index)}
                    disabled={formData.plans.length === 1}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
            <div className="fd-field fd-field-wide">
              <button type="button" className="profile-secondary-btn profile-secondary-btn-sm" onClick={addPlanRow}>
                + Add Plan
              </button>
            </div>
          </div>

          <p className="fd-section-title">Commission Rates</p>
          <div className="fd-modal-grid">
            {COMMISSION_FIELDS.map((field) => (
              <div className="fd-field" key={field.key}>
                <label htmlFor={field.key}>{field.label}</label>
                <input
                  id={field.key}
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  name={field.key}
                  value={formData[field.key]}
                  onChange={handleFormChange}
                  placeholder="0.00"
                />
              </div>
            ))}
          </div>

          <p className="fd-section-title">Notes</p>
          <div className="fd-modal-grid">
            <div className="fd-field fd-field-wide">
              <label htmlFor="notes">Notes (optional)</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleFormChange}
                className="product-notes-textarea"
                placeholder="Any additional notes..."
              />
            </div>
          </div>

          {error && <div className="error-box" style={{ marginTop: 12 }}>{error}</div>}
        </form>
      </FdModal>

      <FdModal
        isOpen={Boolean(viewingProduct)}
        onClose={closeViewDetails}
        eyebrow="Product Setup"
        title={viewingProduct?.product_name || 'Product Details'}
        subtitle="Review plans and commission rates."
        footer={(
          <button type="button" className="fd-btn fd-btn-cancel" onClick={closeViewDetails}>
            Close
          </button>
        )}
      >
        {viewingProduct ? (
          <div className="product-view-content">
            <div className="product-view-status-row">
              <span className="product-view-label">Status</span>
              <span className={`status-badge status-${viewingProduct.is_active ? 'active' : 'inactive'}`}>
                {viewingProduct.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>

            {viewingProduct.notes ? (
              <div className="product-view-notes">
                <span className="product-view-label">Notes</span>
                <p>{viewingProduct.notes}</p>
              </div>
            ) : null}

            <div>
              <p className="fd-section-title">Plans</p>
              {Array.isArray(viewingProduct.plans) && viewingProduct.plans.length > 0 ? (
                <div className="product-view-list">
                  {viewingProduct.plans.map((plan, index) => (
                    <div key={`view-plan-${index}`} className="product-view-list-item">
                      <span>{plan.plan_name}</span>
                      <strong>{plan.plan_premium != null ? `PHP ${Number(plan.plan_premium).toLocaleString()}` : '-'}</strong>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="product-card-empty">No plans configured</p>
              )}
            </div>

            <div>
              <p className="fd-section-title">Commission Rates</p>
              <div className="product-view-list">
                {COMMISSION_FIELDS.map((field) => (
                  <div key={`view-rate-${field.key}`} className="product-view-list-item">
                    <span>{field.label}</span>
                    <strong>{viewingProduct[field.key] != null ? `${viewingProduct[field.key]}%` : '-'}</strong>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </FdModal>
    </div>
  );
}
